import { debounce } from 'lodash-es';
import { useCallback, useEffect, useRef } from 'react';

import { DEFAULT_SEARCH_DEBOUNCE } from 'utils/timeUtils';

/**
 * Keyword search hook
 * - debounces keyword search callback - based on timeout interval (defaults to 250)
 *
 * Note: Uses value comparison instead of a boolean flag to handle React Strict Mode's
 * double-invocation of effects. The callback only fires when text actually changes.
 */
const useDebounceKeywordSearch = (
  text: string,
  onChange: (value: string) => void,
  timeout = DEFAULT_SEARCH_DEBOUNCE
) => {
  const onChangeDebounce = useCallback(keyword => onChange(keyword.trim()), [onChange]);
  const debouncedKeywords = useRef(debounce(onChangeDebounce, timeout));
  const prevTextRef = useRef(text);

  useEffect(() => {
    if (text === prevTextRef.current) {
      return;
    }
    prevTextRef.current = text;

    const debouncedFn = debouncedKeywords.current;
    debouncedFn(text);

    return () => {
      debouncedFn.cancel();
    };
  }, [text]);
};

export default useDebounceKeywordSearch;
